import { assetManager, error, log, __private } from 'cc';
import { DEBUG, DEV } from 'cc/env';
import globalInstance, { GlobalInstanceEnum } from './System/GlobalInstance';
import BaseGameLauncher, { getCurrentCRMSceneConfig } from './BaseGameLauncher';
import Connection, { IBaseRequestResponseDataType } from './Connection/Connection';
import NetworkHttp, { HttpMethod } from './Connection/Network/NetworkHttp';
import Authorizer from './Toolkit/Helper/Authorizer';
import Cryptor from './Toolkit/Helper/Cryptor';
import Delay from './Toolkit/Utils/Delay';
import Parameter from './Toolkit/Utils/Parameter';

// #region Connection define
enum PathName {
    Assets = '/assets',
}

const pathRouteMap = { [PathName.Assets]: { method: HttpMethod.GET, path: '/assets' } };

export interface IRequestResponseDataType extends IBaseRequestResponseDataType {
    [PathName.Assets]: [undefined, AssetData];
}

type AssetData = {
    // 圖片資源的位置
    Host: string;
    Bundle: {
        // CRM Bundle的名稱
        Host: string;
        // MD5值
        Version: string;
    };
};
// #endregion

export async function loadCRM(addPrefabToScene: boolean = true): Promise<void> {
    await globalInstance.waitForGameRegistered(GlobalInstanceEnum.game);
    await globalInstance.game.waitForLaunched;

    if (!getCurrentCRMSceneConfig()) {
        return;
    }

    if (!globalInstance.games.bonus) {
        // get CRM loading info
        const assetData = await getCRMLoadingInfo();
        if (!assetData) {
            return;
        }
        // load CRM bundle
        const crmBundleLoaded = await loadCRMBundle(assetData);
        if (!crmBundleLoaded) {
            return;
        }
        // initialize the bonus module
        globalInstance.game.event.emit(BaseGameLauncher.EVENT_TYPE.INIT_BONUS_MODULE, assetData);
        await Delay.event(BaseGameLauncher.EVENT_TYPE.INIT_BONUS_MODULE_FINISHED, globalInstance.game.event);
    }

    // add prefab to scene
    if (addPrefabToScene) {
        globalInstance.game.event.emit(BaseGameLauncher.EVENT_TYPE.INIT_SCENE);
    }
}

async function loadCRMBundle(assetData: AssetData): Promise<boolean> {
    const isBundleExist = assetManager.getBundle('crm');
    if (isBundleExist) {
        // If the CRM bundle is already loaded, we can skip loading it again.
        log('CRM bundle is already loaded, skipping load.');
        return true;
    }

    let crmBundleURL: string;
    let options: __private._cocos_core_asset_manager_shared__IBundleOptions;
    // 方便於開發時測試，前端可以透過 queryString 來指定 CRM Bundle 的 URL 或 MD5
    if (DEV || globalInstance.game.config.useInternalCRMBundle) {
        crmBundleURL = Parameter.get('crmBundleURL', 'crm');
        options = { version: Parameter.get('crmBundleMD5', null) };
    } else if (DEBUG) {
        crmBundleURL = Parameter.get('crmBundleURL', assetData.Bundle.Host);
        options = { version: Parameter.get('crmBundleMD5', assetData.Bundle.Version) };
    } else {
        crmBundleURL = assetData.Bundle.Host;
        options = { version: assetData.Bundle.Version };
    }

    try {
        await assetManager.loadBundleAsync(crmBundleURL, options);
    } catch (err) {
        if (DEV) {
            throw new Error(`CRM bundle load failed: ${err}`);
        }
        error(`Cannot load CRM bundle: ${crmBundleURL}`, err);
        return false;
    }
    return true;
}

async function getCRMLoadingInfo(): Promise<AssetData | null> {
    const { token, needEncrypt, bonusDomain } = globalInstance.game.config;
    const cryptor = new Cryptor(token, token.length);
    const authorizer = new Authorizer(token);
    const networkHttp = new NetworkHttp(bonusDomain, cryptor, authorizer, pathRouteMap, needEncrypt);
    const connect = new Connection(networkHttp);

    return new Promise((resolve) => {
        connect.on(Connection.EVENT_TYPE.ERROR, (err) => {
            error('crm loading info Connection error:', err);
            resolve(null);
        });
        connect.send(PathName.Assets).then((data) => resolve(data));
    });
}
